import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Container,
  Chip,
  Divider,
  Fade,
  Zoom,
  Slide,
  CardActionArea,
  IconButton,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import Logo from "../components/Logo";
import "../styles/global.css";

// ===== STYLED COMPONENTS & KEYFRAMES =====
const pulseDot = keyframes`
  0% { opacity: 0.4; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0.4; transform: scale(0.8); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const PremiumCard = styled(Card)(({ theme, companycolor }) => ({
  height: "100%",
  background: "rgba(255,255,255,0.02)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.04)",
  borderRadius: "28px",
  transition: "all 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `linear-gradient(135deg, ${companycolor}08, transparent 50%)`,
    opacity: 0,
    transition: "opacity 0.6s ease",
    zIndex: 0,
  },
  "&:hover": {
    transform: "translateY(-16px) scale(1.01)",
    borderColor: `rgba(255,255,255,0.12)`,
    boxShadow: `0 40px 80px rgba(0,0,0,0.6)`,
    background: "rgba(255,255,255,0.05)",
    "&::before": { opacity: 1 },
    "& .card-glow": { opacity: 1 },
    "& .card-arrow": { transform: "translateX(6px) scale(1.1)", opacity: 1 },
    "& .logo-container": { transform: "scale(1.02)" },
  },
}));

const StatCard = styled(Box)({
  background: "rgba(255,255,255,0.02)",
  backdropFilter: "blur(20px)",
  borderRadius: "28px",
  padding: "40px 32px",
  border: "1px solid rgba(255,255,255,0.04)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  transition: "all 0.4s ease",
  "&:hover": {
    boxShadow: "0 30px 80px rgba(0,0,0,0.4)",
    borderColor: "rgba(255,255,255,0.08)",
    transform: "translateY(-4px)",
  },
});

const NavLink = styled(Box)({
  color: "rgba(255,255,255,0.35)",
  fontSize: "0.7rem",
  letterSpacing: "2px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.3s ease",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -6,
    left: "50%",
    transform: "translateX(-50%) scaleX(0)",
    width: 24,
    height: 2,
    background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
    borderRadius: 4,
    transition: "all 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
  },
  "&:hover": {
    color: "#ffffff",
    "&::after": { transform: "translateX(-50%) scaleX(1)" },
  },
});

const SectionTitle = ({ children, subtitle }) => (
  <Box sx={{ textAlign: "center", mb: 5 }}>
    <Typography
      variant="h2"
      sx={{
        fontFamily: "'Georgia', serif",
        fontWeight: "bold",
        fontSize: { xs: "2rem", md: "3rem" },
        color: "#ffffff",
        mb: 1,
        letterSpacing: "-0.5px",
      }}
    >
      {children}
    </Typography>
    {subtitle && (
      <Typography
        sx={{
          color: "rgba(255,255,255,0.2)",
          fontSize: "1rem",
          letterSpacing: "3px",
          fontWeight: "300",
        }}
      >
        {subtitle}
      </Typography>
    )}
    <Box
      sx={{
        width: 60,
        height: 4,
        background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
        mx: "auto",
        mt: 3,
        borderRadius: 4,
        opacity: 0.5,
      }}
    />
  </Box>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Refs for sections
  const homeRef = useRef(null);
  const companiesRef = useRef(null);
  const sectorsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (sectionRef) => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Companies - Added Pentillion and DiamondCoins with correct URLs
  const companies = [
    {
      id: 1,
      name: "Worldwide Communications",
      logo: "/Worldwide communications.jpg",
      description: "Premier telecommunications and digital connectivity provider serving enterprise and consumer markets globally.",
      sector: "Technology",
      founded: "2003",
      status: "Active",
      website: "https://worldwidecommunications.co.za/",
      color: "#00d4ff",
      icon: "📡",
      locations: "28",
      mission: "Connecting the world through innovative communication solutions.",
    },
    {
      id: 2,
      name: "Worldwide Law",
      logo: "/worldwidelaw.png",
      description: "International legal services firm providing corporate law, litigation, and regulatory compliance across multiple jurisdictions.",
      sector: "Legal",
      founded: "2000",
      status: "Active",
      website: "https://worldwidelaw.co.za/",
      color: "#ffd93d",
      icon: "⚖️",
      locations: "45",
      mission: "Delivering justice and legal excellence worldwide.",
    },
    {
      id: 3,
      name: "Worldwide Livestock",
      logo: "/worldwidelivestock.jpg",
      description: "Global leader in livestock management, breeding, and agricultural solutions for sustainable food production.",
      sector: "Agriculture",
      founded: "2006",
      status: "Active",
      website: "https://worldwidelivestock.co.za/",
      color: "#6bcb77",
      icon: "🐄",
      locations: "18",
      mission: "Feeding the world through sustainable agriculture.",
    },
    {
      id: 4,
      name: "Worldwide Agriculture",
      logo: "/worldwide agriculture.jpg",
      description: "Global agricultural solutions provider specializing in sustainable farming, crop management, and food security.",
      sector: "Agriculture",
      founded: "2010",
      status: "Active",
      website: "https://worldwideagriculture.co.za/",
      color: "#4caf50",
      icon: "🌾",
      locations: "22",
      mission: "Revolutionizing agriculture for a food-secure future.",
    },
    {
      id: 5,
      name: "Worldwide Capital",
      logo: "/Worldwide capital.jpg",
      description: "Leading investment and financial services firm specializing in high-yield opportunities and wealth management.",
      sector: "Finance",
      founded: "2005",
      status: "Active",
      website: "https://worldwidecapital.co.za/",
      color: "#ffd700",
      icon: "💰",
      locations: "52",
      mission: "Building wealth and securing financial futures globally.",
    },
    {
      id: 6,
      name: "Pentillion",
      logo: "/pentillion.png",
      description: "Premier cryptocurrency and blockchain investment firm specializing in digital asset management, mining operations, and decentralized finance solutions.",
      sector: "Cryptocurrency",
      founded: "2018",
      status: "Active",
      website: "https://pentillion.com/",
      color: "#7b2ffc",
      icon: "₿",
      locations: "12",
      mission: "Empowering the future of finance through blockchain innovation and digital asset excellence.",
    },
    {
      id: 7,
      name: "DiamondCoins",
      logo: "/diamondcoins.png",
      description: "Leading digital currency exchange and trading platform offering secure, transparent, and user-friendly cryptocurrency services worldwide.",
      sector: "Cryptocurrency",
      founded: "2020",
      status: "Active",
      website: "https://diamondcoin.au/",
      color: "#00e5ff",
      icon: "💎",
      locations: "8",
      mission: "Making cryptocurrency accessible, secure, and profitable for everyone.",
    },
  ];

  const stats = {
    totalCompanies: companies.length,
    sectors: [...new Set(companies.map(c => c.sector))].length,
    founded: "1995",
  };

  // Sectors - Updated with Cryptocurrency
  const sectorData = [
    { name: "Technology", icon: "💻", companies: 1, description: "Innovative tech solutions driving digital transformation." },
    { name: "Legal", icon: "⚖️", companies: 1, description: "Global legal excellence and corporate compliance." },
    { name: "Agriculture", icon: "🌱", companies: 2, description: "Sustainable farming and food security solutions." },
    { name: "Finance", icon: "💰", companies: 1, description: "Wealth management and investment strategies." },
    { name: "Cryptocurrency", icon: "₿", companies: 2, description: "Blockchain innovation and digital asset management." },
  ];

  const handleLogoError = (e) => {
    e.target.src =
      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%2324338a"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="white" font-size="24" font-family="Georgia"%3EW%3C/text%3E%3C/svg%3E';
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "#070b15",
        "&::before": {
          content: '""',
          position: "fixed",
          inset: 0,
          backgroundImage: `url(/mightymenofdavid.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.06,
          zIndex: 0,
        },
        "&::after": {
          content: '""',
          position: "fixed",
          inset: 0,
          background: "radial-gradient(ellipse at 30% 20%, rgba(36, 51, 138, 0.4) 0%, rgba(10, 14, 26, 0.92) 60%, rgba(7, 11, 21, 0.98) 100%)",
          zIndex: 0,
        },
      }}
    >
      {/* ===== PREMIUM NAVIGATION ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          background: "rgba(7, 11, 21, 0.88)",
          backdropFilter: "blur(40px)",
          borderBottom: "1px solid rgba(255,255,255,0.03)",
          px: { xs: 2, md: 6 },
          py: 1.5,
          boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
        }}
      >
        <Container maxWidth="xl" sx={{ px: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Logo size="large" variant="icon" />
              
            </Box>

            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Box sx={{ display: { xs: "none", md: "flex" }, gap: 6 }}>
                <NavLink onClick={() => scrollToSection(homeRef)}>Home</NavLink>
                <NavLink onClick={() => scrollToSection(companiesRef)}>Companies</NavLink>
                <NavLink onClick={() => scrollToSection(sectorsRef)}>Sectors</NavLink>
                <NavLink onClick={() => scrollToSection(aboutRef)}>About</NavLink>
              </Box>

              <Button
                onClick={handleLogout}
                variant="contained"
                size="small"
                sx={{
                  background: "linear-gradient(135deg, #1a2a70, #4a90d9)",
                  color: "#ffffff",
                  fontSize: "0.6rem",
                  letterSpacing: "2px",
                  px: 4,
                  py: 1.25,
                  borderRadius: "50px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  boxShadow: "0 4px 20px rgba(74, 144, 217, 0.15)",
                  border: "1px solid rgba(255,255,255,0.05)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #24338a, #5a9de9)",
                    boxShadow: "0 8px 35px rgba(74, 144, 217, 0.3)",
                    transform: "translateY(-2px) scale(1.02)",
                  },
                  transition: "all 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
                }}
              >
                Sign Out
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ===== HOME SECTION ===== */}
      <Box ref={homeRef}>
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Container maxWidth="xl" sx={{ px: 0 }}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <Fade in timeout={800}>
                  <Box>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 2,
                        background: "rgba(74, 144, 217, 0.06)",
                        border: "1px solid rgba(74, 144, 217, 0.08)",
                        borderRadius: "50px",
                        px: 2.5,
                        py: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "#4a90d9",
                          animation: `${pulseDot} 2s ease-in-out infinite`,
                        }}
                      />
                      <Typography
                        sx={{
                          color: "rgba(165, 164, 206, 0.8)",
                          fontSize: "0.55rem",
                          letterSpacing: "3px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        Since 1995
                      </Typography>
                    </Box>

                    <Typography
                      variant="h1"
                      sx={{
                        fontFamily: "'Georgia', serif",
                        fontWeight: "bold",
                        fontSize: { xs: "2.8rem", md: "5rem" },
                        color: "#ffffff",
                        mb: 2,
                        letterSpacing: "-2px",
                        lineHeight: 1.05,
                      }}
                    >
                      Our Group
                      <br />
                      <Box
                        component="span"
                        sx={{
                          background: "linear-gradient(135deg, #4a90d9, #a5a4ce, #4a90d9)",
                          backgroundSize: "200% auto",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: `${shimmer} 4s linear infinite`,
                        }}
                      >
                        Companies
                      </Box>
                    </Typography>

                    <Box
                      sx={{
                        width: 80,
                        height: 4,
                        background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                        mb: 4,
                        borderRadius: 4,
                        opacity: 0.6,
                      }}
                    />

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.45)",
                        fontSize: { xs: "1rem", md: "1.25rem" },
                        letterSpacing: "0.5px",
                        fontWeight: "300",
                        maxWidth: "580px",
                        lineHeight: 2,
                      }}
                    >
                      A diverse portfolio of industry-leading enterprises
                      driving global progress across multiple sectors.
                    </Typography>

                    {/* BUTTONS REMOVED - Only the decorative space remains */}
                    <Box sx={{ mt: 4, height: "0px" }} />
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={5}>
                <Zoom in timeout={1000}>
                  <StatCard>
                    <Grid container spacing={2}>
                      {[
                        { label: "Companies", value: stats.totalCompanies },
                        { label: "Sectors", value: stats.sectors },
                        { label: "Founded", value: stats.founded },
                      ].map((stat, index) => (
                        <Grid item xs={4} key={index}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              sx={{
                                color: "#4a90d9",
                                fontSize: { xs: "2rem", md: "2.8rem" },
                                fontWeight: "bold",
                                fontFamily: "'Georgia', serif",
                                mb: 0.5,
                                background: "linear-gradient(135deg, #4a90d9, #a5a4ce)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                              }}
                            >
                              {stat.value}
                            </Typography>
                            <Typography
                              sx={{
                                color: "rgba(255,255,255,0.2)",
                                fontSize: "0.5rem",
                                letterSpacing: "3px",
                                textTransform: "uppercase",
                                fontWeight: "600",
                              }}
                            >
                              {stat.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </StatCard>
                </Zoom>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Sectors Bar */}
        <Slide in timeout={600} direction="up">
          <Box
            sx={{
              py: 2.5,
              px: { xs: 2, md: 6 },
              borderTop: "1px solid rgba(255,255,255,0.02)",
              borderBottom: "1px solid rgba(255,255,255,0.02)",
              background: "rgba(255,255,255,0.01)",
              backdropFilter: "blur(10px)",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Container maxWidth="xl" sx={{ px: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: { xs: 2, md: 5 },
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {["Energy", "Technology", "Cryptocurrency", "Communications", "Agriculture", "Finance", "Legal"].map(
                  (sector) => (
                    <Typography
                      key={sector}
                      sx={{
                        color: "rgba(255,255,255,0.15)",
                        fontSize: "0.5rem",
                        letterSpacing: "3px",
                        textTransform: "uppercase",
                        fontWeight: "600",
                        transition: "all 0.4s ease",
                        cursor: "default",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: -4,
                          left: "50%",
                          transform: "translateX(-50%) scaleX(0)",
                          width: 20,
                          height: 2,
                          background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                          borderRadius: 4,
                          transition: "all 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
                        },
                        "&:hover": {
                          color: "rgba(255,255,255,0.6)",
                          "&::after": { transform: "translateX(-50%) scaleX(1)" },
                        },
                      }}
                    >
                      {sector}
                    </Typography>
                  )
                )}
              </Box>
            </Container>
          </Box>
        </Slide>
      </Box>

      {/* ===== COMPANIES SECTION ===== */}
      <Box ref={companiesRef}>
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Container maxWidth="xl" sx={{ px: 0 }}>
            <SectionTitle subtitle="Industry-leading enterprises under the Worldwide Corporation umbrella">
              Our Portfolio
            </SectionTitle>

            <Grid container spacing={4}>
              {companies.map((company, index) => (
                <Grid item xs={12} md={6} lg={4} key={company.id}>
                  <Fade in timeout={600 + index * 100}>
                    <PremiumCard companycolor={company.color}>
                      <Box
                        className="card-glow"
                        sx={{
                          position: "absolute",
                          top: -60,
                          right: -60,
                          width: 180,
                          height: 180,
                          background: `radial-gradient(circle, ${company.color}12, transparent 70%)`,
                          borderRadius: "50%",
                          opacity: 0,
                          transition: "opacity 0.6s ease",
                          pointerEvents: "none",
                          zIndex: 0,
                        }}
                      />

                      <CardActionArea
                        onClick={() => window.open(company.website, "_blank")}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 0 }}>
                          {/* Logo Section */}
                          <Box
                            className="logo-container"
                            sx={{
                              width: "100%",
                              height: 280,
                              overflow: "hidden",
                              background: `linear-gradient(135deg, ${company.color}08, rgba(255,255,255,0.02))`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                              borderBottom: `2px solid ${company.color}15`,
                              transition: "transform 0.6s ease",
                              p: 3,
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                height: "4px",
                                background: `linear-gradient(90deg, ${company.color}, ${company.color}60, ${company.color}20, transparent)`,
                                opacity: 0.8,
                              }}
                            />

                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                background: `radial-gradient(circle at 20% 30%, ${company.color}10, transparent 60%)`,
                                pointerEvents: "none",
                              }}
                            />

                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.5s ease",
                                overflow: "hidden",
                                position: "relative",
                                zIndex: 1,
                              }}
                            >
                              <img
                                src={company.logo}
                                alt={company.name}
                                onError={handleLogoError}
                                style={{
                                  width: "auto",
                                  height: "90%",
                                  maxWidth: "90%",
                                  objectFit: "contain",
                                  transition: "transform 0.4s ease",
                                  filter: "brightness(0.98) contrast(1.02)",
                                }}
                                onMouseEnter={(e) => (e.target.style.transform = "scale(1.08)")}
                                onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                              />
                            </Box>

                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 16,
                                right: 24,
                                fontSize: "2rem",
                                opacity: 0.06,
                                zIndex: 1,
                              }}
                            >
                              {company.icon}
                            </Box>

                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                background: `linear-gradient(135deg, ${company.color}15, transparent 60%)`,
                                opacity: 0,
                                transition: "opacity 0.5s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                                "&:hover": { opacity: 1 },
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#ffffff",
                                  fontSize: "0.8rem",
                                  letterSpacing: "2px",
                                  fontWeight: "600",
                                  background: "rgba(0,0,0,0.7)",
                                  backdropFilter: "blur(12px)",
                                  padding: "14px 36px",
                                  borderRadius: "50px",
                                  border: "1px solid rgba(255,255,255,0.08)",
                                  transform: "translateY(12px)",
                                  transition: "transform 0.4s ease",
                                  "&:hover": { transform: "translateY(0)" },
                                }}
                              >
                                Visit Website →
                              </Typography>
                            </Box>

                            <Typography
                              sx={{
                                position: "absolute",
                                bottom: 16,
                                left: 24,
                                color: `rgba(255,255,255,0.03)`,
                                fontSize: "0.4rem",
                                letterSpacing: "3px",
                                textTransform: "uppercase",
                                fontWeight: "700",
                                zIndex: 1,
                              }}
                            >
                              {company.sector}
                            </Typography>
                          </Box>

                          {/* Content */}
                          <Box sx={{ p: 3.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: "#ffffff",
                                    fontFamily: "'Georgia', serif",
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                    letterSpacing: "0.5px",
                                    mb: 0.75,
                                  }}
                                >
                                  {company.name}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                  <Chip
                                    label={company.sector}
                                    size="small"
                                    sx={{
                                      background: `rgba(74, 144, 217, 0.1)`,
                                      color: "#4a90d9",
                                      fontSize: "0.45rem",
                                      height: 20,
                                      fontWeight: "600",
                                      letterSpacing: "0.5px",
                                      borderRadius: "4px",
                                      border: `1px solid rgba(74, 144, 217, 0.06)`,
                                    }}
                                  />
                                  <Chip
                                    label={company.status}
                                    size="small"
                                    sx={{
                                      background: "rgba(0, 255, 100, 0.04)",
                                      color: "#00ff88",
                                      fontSize: "0.45rem",
                                      height: 20,
                                      fontWeight: "500",
                                      borderRadius: "4px",
                                      border: "1px solid rgba(0, 255, 100, 0.03)",
                                    }}
                                  />
                                  <Chip
                                    label={`${company.locations} Locations`}
                                    size="small"
                                    sx={{
                                      background: "rgba(255,255,255,0.02)",
                                      color: "rgba(255,255,255,0.25)",
                                      fontSize: "0.4rem",
                                      height: 20,
                                      fontWeight: "400",
                                      borderRadius: "4px",
                                      border: "1px solid rgba(255,255,255,0.03)",
                                    }}
                                  />
                                </Box>
                              </Box>
                              <Box
                                className="card-arrow"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  background: `rgba(255,255,255,0.02)`,
                                  border: `1px solid rgba(255,255,255,0.04)`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "1rem",
                                  transition: "all 0.4s ease",
                                  opacity: 0.6,
                                  "&:hover": {
                                    background: `rgba(255,255,255,0.06)`,
                                    transform: "rotate(45deg)",
                                  },
                                }}
                              >
                                →
                              </Box>
                            </Box>

                            <Typography
                              sx={{
                                color: "rgba(255,255,255,0.4)",
                                fontSize: "0.8rem",
                                lineHeight: 1.8,
                                mb: 2,
                                minHeight: 56,
                              }}
                            >
                              {company.description}
                            </Typography>

                            <Box
                              sx={{
                                background: `linear-gradient(90deg, ${company.color}08, transparent)`,
                                borderRadius: "8px",
                                p: 1.5,
                                mb: 2,
                                borderLeft: `3px solid ${company.color}30`,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "rgba(255,255,255,0.35)",
                                  fontSize: "0.5rem",
                                  fontStyle: "italic",
                                  letterSpacing: "0.3px",
                                  lineHeight: 1.5,
                                }}
                              >
                                "{company.mission}"
                              </Typography>
                            </Box>

                            <Divider sx={{ borderColor: "rgba(255,255,255,0.02)", my: 2 }} />

                            <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                              <Box>
                                <Typography
                                  sx={{
                                    color: "rgba(255,255,255,0.15)",
                                    fontSize: "0.4rem",
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                    fontWeight: "600",
                                  }}
                                >
                                  Founded
                                </Typography>
                                <Typography sx={{ color: "#ffffff", fontSize: "0.85rem", fontWeight: "600" }}>
                                  {company.founded}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  sx={{
                                    color: "rgba(255,255,255,0.15)",
                                    fontSize: "0.4rem",
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                    fontWeight: "600",
                                  }}
                                >
                                  Sector
                                </Typography>
                                <Typography sx={{ color: "#ffffff", fontSize: "0.85rem", fontWeight: "600" }}>
                                  {company.sector}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    background: company.color,
                                    boxShadow: `0 0 12px ${company.color}50`,
                                    animation: `${pulseDot} 2s ease-in-out infinite`,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    color: "rgba(255,255,255,0.15)",
                                    fontSize: "0.4rem",
                                    letterSpacing: "1.5px",
                                    textTransform: "uppercase",
                                    fontWeight: "500",
                                  }}
                                >
                                  Live
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </CardActionArea>

                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -40,
                          right: -40,
                          width: 100,
                          height: 100,
                          border: "1px solid rgba(255,255,255,0.01)",
                          borderRadius: "50%",
                          pointerEvents: "none",
                          zIndex: 0,
                        }}
                      />
                    </PremiumCard>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* ===== SECTORS SECTION ===== */}
      <Box ref={sectorsRef}>
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Container maxWidth="xl" sx={{ px: 0 }}>
            <SectionTitle subtitle="Our diverse industry expertise across multiple sectors">
              Sectors
            </SectionTitle>

            <Grid container spacing={4}>
              {sectorData.map((sector, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Fade in timeout={600 + index * 100}>
                    <Paper
                      elevation={0}
                      sx={{
                        background: "rgba(255,255,255,0.02)",
                        backdropFilter: "blur(20px)",
                        borderRadius: "20px",
                        p: 3.5,
                        border: "1px solid rgba(255,255,255,0.04)",
                        transition: "all 0.4s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                          borderColor: "rgba(255,255,255,0.08)",
                          background: "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            background: "rgba(74,144,217,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.8rem",
                            border: "1px solid rgba(74,144,217,0.05)",
                          }}
                        >
                          {sector.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: "bold" }}>
                            {sector.name}
                          </Typography>
                          <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.75rem" }}>
                            {sector.companies} Company{sector.companies > 1 ? "ies" : ""}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.9rem", lineHeight: 1.7 }}>
                        {sector.description}
                      </Typography>
                      <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {companies
                          .filter((c) => c.sector === sector.name)
                          .map((c) => (
                            <Chip
                              key={c.id}
                              label={c.name}
                              size="small"
                              sx={{
                                background: "rgba(255,255,255,0.03)",
                                color: "rgba(255,255,255,0.4)",
                                fontSize: "0.4rem",
                                height: 20,
                                border: "1px solid rgba(255,255,255,0.03)",
                              }}
                            />
                          ))}
                      </Box>
                    </Paper>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* ===== ABOUT SECTION ===== */}
      <Box ref={aboutRef}>
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Container maxWidth="xl" sx={{ px: 0 }}>
            <SectionTitle subtitle="The Worldwide Corporation Story">
              About Us
            </SectionTitle>

            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <Fade in timeout={800}>
                  <Box>
                    <Typography
                      sx={{
                        color: "#ffffff",
                        fontFamily: "'Georgia', serif",
                        fontSize: "2rem",
                        fontWeight: "bold",
                        mb: 2,
                      }}
                    >
                      Building a Global Legacy
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.4)",
                        fontSize: "1rem",
                        lineHeight: 2,
                        mb: 3,
                      }}
                    >
                      Since 1995, Worldwide Corporation has been at the forefront of global business innovation. 
                      Our journey began with a vision to create a conglomerate that would span multiple industries, 
                      driving progress and creating value across continents.
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.4)",
                        fontSize: "1rem",
                        lineHeight: 2,
                        mb: 3,
                      }}
                    >
                      Today, we operate across five key sectors, employing thousands of professionals worldwide. 
                      Our portfolio of companies represents the pinnacle of excellence in their respective fields, 
                      united by a shared commitment to innovation, sustainability, and global impact.
                    </Typography>

                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 3 }}>
                      <Box>
                        <Typography sx={{ color: "#4a90d9", fontSize: "2rem", fontWeight: "bold", fontFamily: "'Georgia', serif" }}>
                          {stats.totalCompanies}
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.65rem", letterSpacing: "2px", textTransform: "uppercase" }}>
                          Companies
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: "#4a90d9", fontSize: "2rem", fontWeight: "bold", fontFamily: "'Georgia', serif" }}>
                          {stats.sectors}
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.65rem", letterSpacing: "2px", textTransform: "uppercase" }}>
                          Sectors
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: "#4a90d9", fontSize: "2rem", fontWeight: "bold", fontFamily: "'Georgia', serif" }}>
                          5
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.65rem", letterSpacing: "2px", textTransform: "uppercase" }}>
                          Industries
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: "#4a90d9", fontSize: "2rem", fontWeight: "bold", fontFamily: "'Georgia', serif" }}>
                          30+
                        </Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.65rem", letterSpacing: "2px", textTransform: "uppercase" }}>
                          Countries
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={6}>
                <Fade in timeout={1000}>
                  <Box>
                    <Paper
                      elevation={0}
                      sx={{
                        background: "rgba(255,255,255,0.02)",
                        backdropFilter: "blur(20px)",
                        borderRadius: "20px",
                        p: 4,
                        border: "1px solid rgba(255,255,255,0.04)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#ffffff",
                          fontFamily: "'Georgia', serif",
                          fontSize: "1.2rem",
                          fontWeight: "bold",
                          mb: 2,
                          textAlign: "center",
                        }}
                      >
                        Our Core Values
                      </Typography>
                      <List>
                        {[
                          { icon: "🌟", text: "Innovation First" },
                          { icon: "🌍", text: "Global Perspective" },
                          { icon: "🤝", text: "Partnership & Trust" },
                          { icon: "🌱", text: "Sustainability" },
                          { icon: "💎", text: "Excellence" },
                        ].map((value, idx) => (
                          <ListItem key={idx} sx={{ px: 0, py: 1.5 }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: "rgba(74,144,217,0.1)", color: "#4a90d9" }}>
                                {value.icon}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography sx={{ color: "#ffffff", fontWeight: "500" }}>
                                  {value.text}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>

                    <Paper
                      elevation={0}
                      sx={{
                        mt: 3,
                        background: "rgba(255,215,0,0.03)",
                        backdropFilter: "blur(20px)",
                        borderRadius: "20px",
                        p: 3,
                        border: "1px solid rgba(255,215,0,0.06)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ fontSize: "2rem" }}>300</Box>
                        <Box>
                          <Typography sx={{ color: "#ffd700", fontWeight: "bold", fontSize: "0.9rem" }}>
                            Join the 300 Mighty Men
                          </Typography>
                          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.75rem" }}>
                            Apply:{" "}
                            <Box
                              component="span"
                              sx={{
                                color: "#ffd700",
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                              onClick={() => (window.location.href = "mailto:300@WorldwideCorporation.com")}
                            >
                              300@WorldwideCorporation.com
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </Fade>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* ===== PREMIUM FOOTER ===== */}
      <Box
        sx={{
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(30px)",
          borderTop: "1px solid rgba(255,255,255,0.02)",
          py: 6,
          px: { xs: 2, md: 6 },
          position: "relative",
          zIndex: 1,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: "linear-gradient(90deg, transparent, rgba(74,144,217,0.06), transparent)",
          },
        }}
      >
        <Container maxWidth="xl" sx={{ px: 0 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8} textAlign="center">
              <Typography
                sx={{
                  color: "#ffffff",
                  fontFamily: "'Georgia', serif",
                  fontWeight: "bold",
                  fontSize: "1.3rem",
                  letterSpacing: "5px",
                  mb: 1,
                  display: "inline-block",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -4,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 50,
                    height: 2,
                    background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                    borderRadius: 4,
                  },
                }}
              >
                WORLDWIDE CORPORATION
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.15)",
                  fontSize: "0.5rem",
                  letterSpacing: "6px",
                  textTransform: "uppercase",
                  mb: 3,
                  mt: 2.5,
                  fontWeight: "500",
                }}
              >
                A Premier Multinational Conglomerate
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, flexWrap: "wrap", mb: 3 }}>
                {["Energy", "Technology", "Cryptocurrency", "Communications", "Agriculture", "Finance", "Legal"].map(
                  (sector) => (
                    <Typography
                      key={sector}
                      sx={{
                        color: "rgba(255,255,255,0.06)",
                        fontSize: "0.4rem",
                        letterSpacing: "3px",
                        textTransform: "uppercase",
                        fontWeight: "500",
                        transition: "all 0.3s ease",
                        "&:hover": { color: "rgba(74,144,217,0.4)" },
                      }}
                    >
                      {sector}
                    </Typography>
                  )
                )}
              </Box>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.02)", mb: 3 }} />
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.03)",
                  fontSize: "0.35rem",
                  letterSpacing: "5px",
                }}
              >
                © {new Date().getFullYear()} Worldwide Corporation. All Rights Reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== 300 MIGHTY MEN CALLOUT ===== */}
      <Box
        sx={{
          position: "fixed",
          bottom: 30,
          left: 30,
          zIndex: 999,
          p: 2.5,
          borderRadius: 4,
          background: "rgba(255,215,0,0.03)",
          border: "1px solid rgba(255,215,0,0.06)",
          backdropFilter: "blur(20px)",
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 2,
          transition: "all 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
          animation: `${float} 4s ease-in-out infinite`,
          "&:hover": {
            background: "rgba(255,215,0,0.06)",
            borderColor: "rgba(255,215,0,0.12)",
            transform: "translateY(-6px) scale(1.02)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          },
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            background: "rgba(255,215,0,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.4rem",
            border: "1px solid rgba(255,215,0,0.06)",
          }}
        >
          300
        </Box>
        <Box>
          <Typography
            sx={{
              color: "#ffd700",
              fontSize: "0.7rem",
              fontWeight: "bold",
              letterSpacing: "0.5px",
            }}
          >
            Recruiting the 300 Mighty Men
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.25)",
              fontSize: "0.5rem",
              letterSpacing: "0.5px",
              mt: 0.25,
            }}
          >
            Apply:{" "}
            <Box
              component="span"
              sx={{
                color: "#ffd700",
                cursor: "pointer",
                fontWeight: "500",
                transition: "all 0.3s ease",
                "&:hover": {
                  color: "#ffed4a",
                  textDecoration: "underline",
                },
              }}
              onClick={() => (window.location.href = "mailto:300@WorldwideCorporation.com")}
            >
              300@WorldwideCorporation.com
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* ===== APPLICATION FORM LINK (BOTTOM RIGHT) ===== */}
      <Box
        sx={{
          position: "fixed",
          bottom: 30,
          right: 30,
          zIndex: 999,
          p: 2,
          borderRadius: 4,
          background: "rgba(74, 144, 217, 0.05)",
          border: "1px solid rgba(74, 144, 217, 0.08)",
          backdropFilter: "blur(20px)",
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 1.5,
          transition: "all 0.5s cubic-bezier(0.23, 1, 0.32, 1)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
          cursor: "pointer",
          "&:hover": {
            background: "rgba(74, 144, 217, 0.1)",
            borderColor: "rgba(74, 144, 217, 0.2)",
            transform: "translateY(-4px) scale(1.02)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          },
        }}
        onClick={() => navigate("/mighty-men-apply")}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(74, 144, 217, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.2rem",
            border: "1px solid rgba(74, 144, 217, 0.08)",
          }}
        >
          📝
        </Box>
        <Box>
          <Typography
            sx={{
              color: "#4a90d9",
              fontSize: "0.65rem",
              fontWeight: "bold",
              letterSpacing: "0.5px",
            }}
          >
            Application Form
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.2)",
              fontSize: "0.45rem",
              letterSpacing: "0.5px",
              mt: 0.25,
            }}
          >
            Click to Apply →
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;